import { TrendingUp, TrendingDown, Wallet, Clock } from 'lucide-react';
import type {
    FinancialStats,
    Student,
    Staff,
    Account,
    BankAccount,
    Deposit,
    Expense,
    PettyCashRequest,
    CapitalDeposit,
    Transfer,
    JournalEntry,
    Transaction,
    ChartDataPoint,
    School,
} from '../../types';

// PAUD Sub-Schools List
export const paudSchools: School[] = [
    {
        id: 'paud-pekanbaru',
        name: 'PAUD Al-Azhar Pekanbaru',
        income: '45.5 Jt',
        expense: '15.2 Jt',
        balance: '30.3 Jt',
        receivable: '2.5 Jt',
        color: 'bg-primary-soft text-primary',
        icon: TrendingUp
    },
    {
        id: 'paud-kampar',
        name: 'PAUD Al-Azhar Kampar',
        income: '38.2 Jt',
        expense: '12.4 Jt',
        balance: '25.8 Jt',
        receivable: '4.2 Jt',
        color: 'bg-primary-soft text-primary',
        icon: TrendingUp
    },
    {
        id: 'paud-siak',
        name: 'PAUD Al-Azhar Siak',
        income: '36.8 Jt',
        expense: '17.6 Jt',
        balance: '19.2 Jt',
        receivable: '1.8 Jt',
        color: 'bg-primary-soft text-primary',
        icon: TrendingUp
    },
    {
        id: 'paud-sorek',
        name: 'PAUD Al-Azhar Sorek',
        income: '25.5 Jt',
        expense: '8.2 Jt',
        balance: '17.3 Jt',
        receivable: '0.5 Jt',
        color: 'bg-primary-soft text-primary',
        icon: TrendingUp
    },
];

// PAUD Unit Financial Statistics
export const paudStats: FinancialStats[] = [
    {
        label: 'Pendapatan Unit',
        value: 'Rp 120.500.000',
        change: '+8.2%',
        icon: TrendingUp,
    },
    {
        label: 'Pengeluaran Unit',
        value: 'Rp 45.200.000',
        change: '+3.1%',
        icon: TrendingDown,
    },
    {
        label: 'Saldo Kas Unit',
        value: 'Rp 75.300.000',
        change: '+12.4%',
        icon: Wallet,
    },
    {
        label: 'Piutang Siswa',
        value: 'Rp 8.500.000',
        change: '-5.1%',
        icon: Clock,
    },
];

// PAUD Students Data
export const paudStudents: Student[] = [
    {
        id: 'PAUD001',
        name: 'Aisyah Putri Ramadhani',
        parentName: 'Iwan Setiawan',
        class: 'TK A',
        status: 'active',
        tuitionStatus: 'paid',
        totalBill: 'Rp 4.000.000',
        receivable: 'Rp 0',
        schoolId: 'paud-pekanbaru',
        paymentHistory: [
            { id: 'PH_P01', date: '2025-02-01', amount: 'Rp 500.000', method: 'Transfer', status: 'success', receiptNo: 'RCP/PAUD/250201', category: 'SPP Bulan Februari', notes: 'Pembayaran SPP Rutin untuk semester ganjil TA 2024/2025. Terimakasih telah melakukan pembayaran tepat waktu.', attachments: ['item_1.pdf'] },
            { id: 'PH_P02', date: '2025-01-02', amount: 'Rp 500.000', method: 'Cash', status: 'success', receiptNo: 'RCP/PAUD/250102', category: 'SPP Bulan Januari', notes: 'Diterima oleh bendahara sekolah di kantor.' },
            { id: 'PH_P03', date: '2024-12-01', amount: 'Rp 500.000', method: 'Transfer', status: 'success', receiptNo: 'RCP/PAUD/241201', category: 'SPP Bulan Desember' },
            { id: 'PH_P04', date: '2024-11-03', amount: 'Rp 500.000', method: 'Transfer', status: 'success', receiptNo: 'RCP/PAUD/241103', category: 'SPP Bulan November' },
            { id: 'PH_P05', date: '2024-10-01', amount: 'Rp 1.000.000', method: 'Cash', status: 'success', receiptNo: 'RCP/PAUD/241001', category: 'Uang Pangkal', notes: 'Pembayaran Uang Pangkal Siswa Baru.', attachments: ['kwitansi_pangkal.jpg', 'form_registrasi.pdf'] },
            { id: 'PH_P06', date: '2024-09-02', amount: 'Rp 500.000', method: 'Transfer', status: 'success', receiptNo: 'RCP/PAUD/240902', category: 'SPP Bulan September' },
            { id: 'PH_P07', date: '2024-08-01', amount: 'Rp 300.000', method: 'Transfer', status: 'success', receiptNo: 'RCP/PAUD/240801', category: 'Kegiatan Outbound' },
            { id: 'PH_P08', date: '2024-07-05', amount: 'Rp 200.000', method: 'Cash', status: 'success', receiptNo: 'RCP/PAUD/240705', category: 'Seragam Sekolah', notes: 'Pembayaran Seragam Sekolah Olahraga dan Batik.' },
        ]
    },
    {
        id: 'PAUD002',
        name: 'Muhammad Rizki Pratama',
        parentName: 'Rudi Hartono',
        class: 'TK B',
        status: 'active',
        tuitionStatus: 'paid',
        totalBill: 'Rp 500.000',
        receivable: 'Rp 0',
        schoolId: 'paud-kampar',
        paymentHistory: [
            { id: 'PH_P02', date: '2025-02-03', amount: 'Rp 500.000', method: 'Transfer', status: 'success', receiptNo: 'RCP/PAUD/002', category: 'SPP Bulan Februari' },
        ]
    },
    {
        id: 'PAUD003',
        name: 'Zahra Amelia Putri',
        parentName: 'Fitriani',
        class: 'TK A',
        status: 'active',
        tuitionStatus: 'pending',
        amount: 'Rp 850.000',
        totalBill: 'Rp 1.000.000',
        receivable: 'Rp 850.000',
        schoolId: 'paud-siak',
        paymentHistory: []
    },
    {
        id: 'PAUD004',
        name: 'Ahmad Fauzan',
        parentName: 'Dedi Kurniawan',
        class: 'Kelompok Bermain',
        status: 'active',
        tuitionStatus: 'overdue',
        amount: 'Rp 1.700.000',
        totalBill: 'Rp 1.700.000',
        receivable: 'Rp 1.700.000',
        schoolId: 'paud-pekanbaru',
        paymentHistory: []
    },
];

// PAUD Staff Data
export const paudStaff: Staff[] = [
    {
        id: 'STF-PAUD-001',
        nipn: '198001012010012001',
        name: 'Siti Nurhaliza, S.Pd',
        position: 'Kepala Sekolah',
        department: 'Manajemen',
        status: 'active',
        schoolId: 'paud-pekanbaru',
    },
    {
        id: 'STF-PAUD-002',
        nipn: '198505152015072002',
        name: 'Dewi Lestari, S.Pd.AUD',
        position: 'Guru TK A',
        department: 'Pengajaran',
        status: 'active',
        schoolId: 'paud-kampar',
    },
    {
        id: 'STF-PAUD-003',
        nipn: '199009202020012003',
        name: 'Rina Marlina, S.Pd',
        position: 'Guru TK B',
        department: 'Pengajaran',
        status: 'active',
        schoolId: 'paud-siak',
    },
    {
        id: 'STF-PAUD-004',
        nipn: '199203102022011004',
        name: 'Budi Santoso',
        position: 'Tenaga Administrasi',
        department: 'Administrasi',
        status: 'active',
        schoolId: 'paud-pekanbaru',
    },
    {
        id: 'STF-PAUD-005',
        nipn: '198811122018012005',
        name: 'Andi Wijaya',
        position: 'Bendahara Unit',
        department: 'Keuangan',
        status: 'active',
        schoolId: 'paud-pekanbaru',
    },
    {
        id: 'STF-PAUD-006',
        nipn: '199506052023011006',
        name: 'Hendra Saputra',
        position: 'Guru Olahraga',
        department: 'Pengajaran',
        status: 'active',
        schoolId: 'paud-kampar',
    },
    {
        id: 'STF-PAUD-007',
        nipn: '199304152021072007',
        name: 'Maya Sartika',
        position: 'Staff IT',
        department: 'IT Support',
        status: 'active',
        schoolId: 'paud-siak',
    },
    {
        id: 'STF-PAUD-008',
        nipn: '198208122012011008',
        name: 'Sutrisno',
        position: 'Security',
        department: 'Operasional',
        status: 'active',
        schoolId: 'paud-pekanbaru',
    },
];

// PAUD Pekanbaru Accounts
export const paudPekanbaruAccounts: Account[] = [
    {
        id: 'ACC-PKU-1000',
        code: '1-1000',
        name: 'Kas PAUD Pekanbaru',
        type: 'asset',
        category: 'asset',
        balanceType: 'debit',
        status: 'active',
        balance: 'Rp 45.200.000',
    },
    {
        id: 'ACC-PKU-1100',
        code: '1-1100',
        name: 'Bank BCA Pekanbaru',
        type: 'asset',
        category: 'asset',
        balanceType: 'debit',
        status: 'active',
        balance: 'Rp 30.100.000',
    },
    {
        id: 'ACC-PKU-4000',
        code: '4-1000',
        name: 'Pendapatan SPP Pekanbaru',
        type: 'income',
        category: 'income',
        balanceType: 'credit',
        status: 'active',
        balance: 'Rp 95.000.000',
    },
];

// PAUD Kampar Accounts
export const paudKamparAccounts: Account[] = [
    {
        id: 'ACC-KMR-1000',
        code: '1-1000',
        name: 'Kas PAUD Kampar',
        type: 'asset',
        category: 'asset',
        balanceType: 'debit',
        status: 'active',
        balance: 'Rp 25.800.000',
    },
    {
        id: 'ACC-KMR-1100',
        code: '1-1100',
        name: 'Bank Riau Kepri Kampar',
        type: 'asset',
        category: 'asset',
        balanceType: 'debit',
        status: 'active',
        balance: 'Rp 15.400.000',
    },
    {
        id: 'ACC-KMR-4000',
        code: '4-1000',
        name: 'Pendapatan SPP Kampar',
        type: 'income',
        category: 'income',
        balanceType: 'credit',
        status: 'active',
        balance: 'Rp 38.200.000',
    },
];

// PAUD Siak Accounts
export const paudSiakAccounts: Account[] = [
    {
        id: 'ACC-SIAK-1000',
        code: '1-1000',
        name: 'Kas PAUD Siak',
        type: 'asset',
        category: 'asset',
        balanceType: 'debit',
        status: 'active',
        balance: 'Rp 19.200.000',
    },
    {
        id: 'ACC-SIAK-1100',
        code: '1-1100',
        name: 'Bank Syariah Siak',
        type: 'asset',
        category: 'asset',
        balanceType: 'debit',
        status: 'active',
        balance: 'Rp 12.500.000',
    },
    {
        id: 'ACC-SIAK-4000',
        code: '4-1000',
        name: 'Pendapatan SPP Siak',
        type: 'income',
        category: 'income',
        balanceType: 'credit',
        status: 'active',
        balance: 'Rp 36.800.000',
    },
];

// Fallback for generic 'paud' slug
export const paudAccounts = paudPekanbaruAccounts;

// PAUD Bank Accounts
export const paudPekanbaruBankAccounts: BankAccount[] = [
    { id: 'B-PKU-01', bankName: 'BCA', accountNumber: '8220******', accountName: 'PAUD AL-AZHAR PKU', balance: 'Rp 45.200.000', type: 'Operasional', linkedAccount: '1-1100', status: 'active' },
    { id: 'B-PKU-02', bankName: 'Mandiri', accountNumber: '1080******', accountName: 'PAUD AL-AZHAR PKU 2', balance: 'Rp 15.000.000', type: 'Tabungan', linkedAccount: '1-1110', status: 'active' },
];

export const paudKamparBankAccounts: BankAccount[] = [
    { id: 'B-KMR-01', bankName: 'BRI', accountNumber: '0021******', accountName: 'PAUD AL-AZHAR KMR', balance: 'Rp 25.800.000', type: 'Operasional', linkedAccount: '1-1100', status: 'active' },
];

export const paudSiakBankAccounts: BankAccount[] = [
    { id: 'B-SIAK-01', bankName: 'BSI', accountNumber: '7110******', accountName: 'PAUD AL-AZHAR SIAK', balance: 'Rp 19.200.000', type: 'Operasional', linkedAccount: '1-1100', status: 'active' },
];

// PAUD Deposits (Setoran)
export const paudPekanbaruDeposits: Deposit[] = [
    { id: 'D-PKU-01', date: '2025-02-04', type: 'masuk', source: 'SPP Siswa', paymentMethod: 'Transfer BCA', amount: 'Rp 12.500.000', description: 'Pelunasan SPP 10 Siswa', bankAccountId: 'B-PKU-01', status: 'completed' },
    { id: 'D-PKU-02', date: '2025-02-03', type: 'masuk', source: 'Uang Pangkal', paymentMethod: 'Tunai', amount: 'Rp 5.000.000', description: 'Siswa Baru - Ahmad', bankAccountId: 'B-PKU-01', status: 'completed' },
];

export const paudKamparDeposits: Deposit[] = [
    { id: 'D-KMR-01', date: '2025-02-04', type: 'masuk', source: 'SPP Siswa', paymentMethod: 'Transfer BRI', amount: 'Rp 8.200.000', description: 'Pelunasan SPP 6 Siswa', bankAccountId: 'B-KMR-01', status: 'completed' },
];

export const paudSiakDeposits: Deposit[] = [
    { id: 'D-SIAK-01', date: '2025-02-04', type: 'masuk', source: 'Hibah', paymentMethod: 'Transfer BSI', amount: 'Rp 2.000.000', description: 'Donasi Alat Tulis', bankAccountId: 'B-SIAK-01', status: 'completed' },
];

// PAUD Expenses (Pengeluaran)
export const paudPekanbaruExpenses: Expense[] = [
    { id: 'E-PKU-01', date: '2025-02-05', category: 'Listrik & Air', account: '5-1001', amount: 'Rp 1.200.000', method: 'Auto-Debit', status: 'approved' },
    { id: 'E-PKU-02', date: '2025-02-04', category: 'Alat Tulis Kantor', account: '5-1002', amount: 'Rp 450.000', method: 'Tunai', status: 'approved' },
];

export const paudKamparExpenses: Expense[] = [
    { id: 'E-KMR-01', date: '2025-02-05', category: 'Gaji Guru', account: '5-1000', amount: 'Rp 8.000.000', method: 'Transfer', status: 'approved' },
];

export const paudSiakExpenses: Expense[] = [
    { id: 'E-SIAK-01', date: '2025-02-05', category: 'Sewa Gedung', account: '5-1003', amount: 'Rp 5.000.000', method: 'Transfer', status: 'pending' },
];

// PAUD Petty Cash (Kas Laci)
export const paudPekanbaruPettyCash: PettyCashRequest[] = [
    { id: 'PC-PKU-01', date: '2025-02-05', applicant: 'Siti (Admin)', amount: 'Rp 200.000', purpose: 'Konsumsi Rapat Guru', status: 'pending' },
    { id: 'PC-PKU-02', date: '2025-02-04', applicant: 'Dewi (Guru)', amount: 'Rp 100.000', purpose: 'Bahan Prakarya TK A', status: 'approved' },
];

export const paudKamparPettyCash: PettyCashRequest[] = [
    { id: 'PC-KMR-01', date: '2025-02-05', applicant: 'Budi (Admin)', amount: 'Rp 150.000', purpose: 'Bensin Operasional', status: 'approved' },
];

export const paudSiakPettyCash: PettyCashRequest[] = [
    { id: 'PC-SIAK-01', date: '2025-02-05', applicant: 'Rina (Guru)', amount: 'Rp 300.000', purpose: 'Peralatan Kebersihan', status: 'rejected' },
];

// PAUD Capital Deposits (Setoran Modal)
export const paudPekanbaruCapitalDeposits: CapitalDeposit[] = [
    { id: 'CD-PKU-01', date: '2025-01-15', source: 'Yayasan Pusat', targetAccount: '3-1000', amount: 'Rp 50.000.000', description: 'Modal Kerja Awal Tahun' },
];

export const paudKamparCapitalDeposits: CapitalDeposit[] = [
    { id: 'CD-KMR-01', date: '2025-01-20', source: 'Donatur Tetap', targetAccount: '3-1000', amount: 'Rp 25.000.000', description: 'Pembangunan Taman Bermain' },
];

export const paudSiakCapitalDeposits: CapitalDeposit[] = [
    { id: 'CD-SIAK-01', date: '2025-01-25', source: 'Yayasan Pusat', targetAccount: '3-1000', amount: 'Rp 30.000.000', description: 'Subsidi Operasional Siak' },
];

// Fallbacks
export const paudBankAccounts = paudPekanbaruBankAccounts;
export const paudDeposits = paudPekanbaruDeposits;
export const paudExpenses = paudPekanbaruExpenses;
export const paudPettyCash = paudPekanbaruPettyCash;
export const paudCapitalDeposits = paudPekanbaruCapitalDeposits;

// PAUD Transfers
export const paudTransfers: Transfer[] = [
    {
        id: 'TRF-001',
        date: '2025-02-01',
        fromUnit: 'PAUD',
        toUnit: 'SD',
        amount: 'Rp 2.000.000',
        description: 'Transfer subsidi pembelian buku bersama',
        status: 'completed',
    },
    // ... rest of the file
    {
        id: 'TRF-002',
        date: '2025-01-28',
        fromUnit: 'PAUD',
        toUnit: 'SMP',
        amount: 'Rp 500.000',
        description: 'Iuran kegiatan HUT Yayasan',
        status: 'completed',
    },
    {
        id: 'TRF-003',
        date: '2025-01-20',
        fromUnit: 'PAUD',
        toUnit: 'SMA',
        amount: 'Rp 1.000.000',
        description: 'Bantuan biaya perbaikan gerbang utama',
        status: 'completed',
    },
    {
        id: 'TRF-004',
        date: '2025-01-10',
        fromUnit: 'PAUD',
        toUnit: 'Pusat',
        amount: 'Rp 15.000.000',
        description: 'Setoran Surplus Semester Ganjil',
        status: 'completed',
    },
    {
        id: 'TRF-005',
        date: '2025-01-05',
        fromUnit: 'PAUD',
        toUnit: 'TK-B',
        amount: 'Rp 300.000',
        description: 'Bantuan operasional mendesak unit',
        status: 'completed',
    },
];

// PAUD Journal Entries
export const paudJournalEntries: JournalEntry[] = [
    {
        id: 'JRN-001',
        date: '2025-02-01',
        description: 'Penerimaan SPP Februari',
        debitAccount: 'Kas (1-1000)',
        creditAccount: 'Pendapatan SPP (4-1000)',
        amount: 'Rp 5.500.000',
        reference: 'SPP-FEB-2025',
    },
    {
        id: 'JRN-002',
        date: '2025-01-31',
        description: 'Pembayaran Gaji Januari',
        debitAccount: 'Beban Gaji (5-1000)',
        creditAccount: 'Kas (1-1000)',
        amount: 'Rp 14.000.000',
        reference: 'GAJI-JAN-2025',
    },
];

// PAUD Recent Transactions
export const paudTransactions: Transaction[] = [
    {
        id: 'TRX-PAUD-001',
        type: 'income',
        description: 'Pembayaran SPP Februari',
        amount: 'Rp 5.500.000',
        date: '2025-02-01',
        category: 'SPP',
    },
    {
        id: 'TRX-PAUD-002',
        type: 'expense',
        description: 'Pembelian Alat Peraga Edukatif',
        amount: 'Rp 1.200.000',
        date: '2025-01-31',
        category: 'Operasional',
    },
    {
        id: 'TRX-PAUD-003',
        type: 'income',
        description: 'Uang Pangkal Siswa Baru',
        amount: 'Rp 3.000.000',
        date: '2025-01-30',
        category: 'Uang Pangkal',
    },
];

// PAUD Financial Chart Data (Generic fallback)
export const paudChartData: ChartDataPoint[] = [
    { month: 'Mar', income: 17500000, expense: 6800000 },
    { month: 'Apr', income: 18000000, expense: 7000000 },
    { month: 'Mei', income: 19500000, expense: 7500000 },
    { month: 'Jun', income: 16800000, expense: 9200000 },
    { month: 'Jul', income: 25500000, expense: 8500000 },
    { month: 'Agu', income: 21000000, expense: 7800000 },
    { month: 'Sep', income: 18500000, expense: 7200000 },
    { month: 'Okt', income: 19200000, expense: 7500000 },
    { month: 'Nov', income: 18800000, expense: 7100000 },
    { month: 'Des', income: 21000000, expense: 8200000 },
    { month: 'Jan', income: 19500000, expense: 7800000 },
    { month: 'Feb', income: 23500000, expense: 7400000 },
];

export const paudPekanbaruChartData: ChartDataPoint[] = paudChartData.map(d => ({
    ...d,
    income: d.income * 1.2,
    expense: d.expense * 0.9
}));

export const paudKamparChartData: ChartDataPoint[] = paudChartData.map(d => ({
    ...d,
    income: d.income * 0.8,
    expense: d.expense * 1.1
}));

export const paudSiakChartData: ChartDataPoint[] = paudChartData.map(d => ({
    ...d,
    income: d.income * 0.7,
    expense: d.expense * 0.6
}));
